import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import WorkCard from './ui/WorkCard';
import barterLogo from '../assets/barter_full_logo.svg';
import barterImg from '../assets/barter.png';
import ammopantryImg from '../assets/ammopantryai_dashboard.png';
import yapaiImg from '../assets/yapai_feedback.png';

const ammopantryLogo = process.env.PUBLIC_URL + '/ammopantryai_icon.png';
const yapaiLogo = process.env.PUBLIC_URL + '/yapai_icon.svg';
const edifiLogo = process.env.PUBLIC_URL + '/edifi_icon.png';

const buildWithLimitsLogo = process.env.PUBLIC_URL + '/buildwithlimits_icon.png';

const projects = [
    {
        id: 0,
        logo: buildWithLimitsLogo,
        name: 'Build With Limits',
        description: 'My personal brand and storefront for health, fitness, and motherhood systems. Programs, gear recommendations, and the playbook for building while raising kids.',
        tech: ['React', 'Tailwind CSS', 'Firebase'],
        status: 'Live',
        statusColor: 'bg-green-100 text-green-700',
        link: 'https://buildwithlimits.com',
        linkText: 'buildwithlimits.com',
        img: 'https://buildwithlimits.com/og.png',
        appStore: null,
    },
    {
        id: 1,
        logo: ammopantryLogo,
        name: 'AmmoPantryAI',
        description: 'AI-powered inventory management for hunters, shooters, and preppers. Snap a photo of any ammo box and AI instantly identifies and logs it. Tracks ammo, firearms, reloading materials, and full emergency/prepper supplies with family sharing.',
        tech: ['React Native', 'Expo', 'Firebase', 'OpenAI', 'RevenueCat'],
        status: 'Live on App Store',
        statusColor: 'bg-green-100 text-green-700',
        link: 'https://ammopantryai.app',
        linkText: 'ammopantryai.app',
        img: ammopantryImg,
        video: process.env.PUBLIC_URL + '/ammopantryai_demo.mp4',
        appStore: 'https://apps.apple.com/us/app/ammopantryai/id6747729414',
    },
    {
        id: 2,
        logo: yapaiLogo,
        name: 'YapAI',
        description: 'Communication coaching powered by AI. Record real conversations, get transcribed with speaker diarization, and receive structured Stop/Keep/Start feedback with tonality scores, alternative phrasings, and long-term progress tracking.',
        tech: ['React Native', 'Expo', 'Firebase', 'OpenAI', 'Deepgram'],
        status: 'Waitlist Open',
        statusColor: 'bg-terra/10 text-terra',
        link: 'https://yapai.app',
        linkText: 'yapai.app',
        img: yapaiImg,
        video: process.env.PUBLIC_URL + '/yapai_demo.mp4',
        appStore: null,
    },
    {
        id: 3,
        logo: edifiLogo,
        name: 'Edifi — Family Health OS',
        description: 'A family health operating system that replaces the clipboard. Enter your family\'s health profiles once — conditions, medications, allergies, wearables data — and Edifi pre-fills every intake form, generates AI-personalized supplement recommendations via Claude, and hands off a complete clinical summary to any doctor via QR code (no app, no login, no EHR integration required). Built full-stack POC in 48 hours using Scope Flow workflow and Gstack to rapidly ship a React Native mobile app, Expo API routes, and a Vite web receiver.',
        tech: ['React Native', 'Expo', 'Firebase', 'Claude API', 'OpenAI', 'TypeScript', 'Google Cloud'],
        status: 'YC S26 Application',
        statusColor: 'bg-orange-100 text-orange-700',
        link: null,
        linkText: null,
        img: null,
        appStore: null,
        isYC: true,
    },
    {
        id: 4,
        logo: barterLogo,
        name: 'Barter',
        description: 'An AI-first bartering marketplace where users trade goods and services without money. AI matches offers, suggests fair trades, and powers the discovery engine.',
        tech: ['React', 'Node.js', 'AI/ML'],
        status: 'Live',
        statusColor: 'bg-green-100 text-green-700',
        link: 'https://gobarter.io',
        linkText: 'gobarter.io',
        img: barterImg,
        appStore: null,
    },
];

const WorkPage = () => {
    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <title>Work — Amanda Filomena | AI Products & Software</title>
                    <link rel="canonical" href="https://amandafilomena.com/work" />
                    <meta name="description" content="AI products and software projects built by Amanda Filomena — AmmoPantry AI, Yap AI, Edifi, and more." />
                    <meta property="og:title" content="Work — Amanda Filomena" />
                    <meta property="og:description" content="AI products and software I've built end-to-end — from idea to App Store." />
                    <meta property="og:url" content="https://amandafilomena.com/work" />
                    <meta property="og:type" content="website" />
                    <script type="application/ld+json">{JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "SoftwareApplication",
                                "name": "AmmoPantry AI",
                                "url": "https://ammopantryai.app",
                                "applicationCategory": "UtilitiesApplication",
                                "operatingSystem": "iOS",
                                "description": "AI-powered inventory management for hunters, shooters, and preppers. Snap a photo of any ammo box and AI instantly identifies and logs it.",
                                "author": { "@type": "Person", "name": "Amanda Filomena Anderson" },
                                "downloadUrl": "https://apps.apple.com/us/app/ammopantryai/id6747729414",
                                "offers": { "@type": "Offer", "availability": "https://schema.org/InStock" }
                            },
                            {
                                "@type": "SoftwareApplication",
                                "name": "Yap AI",
                                "url": "https://yapai.app",
                                "applicationCategory": "LifestyleApplication",
                                "operatingSystem": "iOS",
                                "description": "Communication coaching app. Record real conversations, get AI-structured Stop/Keep/Start feedback with tonality scores and long-term progress tracking.",
                                "author": { "@type": "Person", "name": "Amanda Filomena Anderson" }
                            },
                            {
                                "@type": "SoftwareApplication",
                                "name": "Barter",
                                "url": "https://gobarter.io",
                                "applicationCategory": "ShoppingApplication",
                                "operatingSystem": "Web",
                                "description": "AI-first bartering marketplace where users trade goods and services without money. AI matches offers and suggests fair trades.",
                                "author": { "@type": "Person", "name": "Amanda Filomena Anderson" }
                            },
                            {
                                "@type": "SoftwareApplication",
                                "name": "Edifi",
                                "applicationCategory": "HealthApplication",
                                "operatingSystem": "iOS",
                                "description": "Family Health OS that replaces the clipboard. AI-personalized health recommendations via Claude. Doctor handoff via QR code. Built in 48 hours for YC S26.",
                                "author": { "@type": "Person", "name": "Amanda Filomena Anderson" }
                            }
                        ]
                    })}</script>
                </Helmet>

                <main className="mx-auto max-w-6xl px-6 py-20 md:py-28">
                    <div className="mb-16 opacity-0 animate-fade-up">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I'm Building</p>
                        <h1 className="text-3xl md:text-4xl text-ink mb-4" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>Work</h1>
                        <p className="text-base text-muted max-w-xl">
                            AI products and software I've built end-to-end — from idea to App Store.
                        </p>
                    </div>

                    <div className="flex flex-col gap-12">
                        {projects.map((p, i) => (
                            <WorkCard
                                key={p.id}
                                status={p.status}
                                name={p.name}
                                description={p.description}
                                tech={p.tech}
                                link={p.link}
                                linkText={p.linkText}
                                appStore={p.appStore}
                                img={p.img}
                                video={p.video}
                                logo={p.logo}
                                isYC={p.isYC}
                                stats={p.isYC ? [
                                    { value: '48h', label: 'Built from scratch' },
                                    { value: '8k', label: 'Lines of TypeScript' },
                                    { value: '3', label: 'Apps shipped' },
                                ] : null}
                            />
                        ))}
                    </div>
                </main>

                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                GitHub
                            </a>
                            <a href="mailto:hello@amandafilomena.com" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Contact
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default WorkPage;
